/**
 * Advanced Search Query Builder
 * Utilities for building type-safe filterGroups queries
 */

import {
  AdvancedSearchQuery,
  FilterOperator,
  FilterRule,
  FilterGroup,
  LogicalOperator,
} from '../types/advancedSearch';

/**
 * Build a simple single-field filter query
 * Used by async select components for search and value lookup
 *
 * @param field - Field name to filter on
 * @param operator - Filter operator (contains, equals, etc.)
 * @param value - Filter value
 * @returns Complete AdvancedSearchQuery structure
 *
 * @example
 * buildSimpleFilterQuery('grade_name', 'contains', 'GMP')
 * // Returns:
 * // {
 * //   filterGroups: [{
 * //     id: '1',
 * //     logicalOperator: 'AND',
 * //     rules: [{
 * //       id: '1',
 * //       field: 'grade_name',
 * //       operator: 'contains',
 * //       value: 'GMP'
 * //     }]
 * //   }],
 * //   groupOperator: 'AND'
 * // }
 */
export function buildSimpleFilterQuery(
  field: string,
  operator: FilterOperator,
  value: string
): AdvancedSearchQuery {
  return {
    filterGroups: [
      {
        id: '1',
        logicalOperator: 'AND',
        rules: [
          {
            id: '1',
            field,
            operator,
            value,
          },
        ],
      },
    ],
    groupOperator: 'AND',
  };
}

/**
 * Build a multi-field filter query with AND logic
 * Useful for complex filtering scenarios
 *
 * @param rules - Array of filter rules
 * @param groupOperator - Operator between groups (default: 'AND')
 * @returns Complete AdvancedSearchQuery structure
 *
 * @example
 * buildMultiFieldQuery([
 *   { field: 'product_name', operator: 'contains', value: 'acetone' },
 *   { field: 'grade_name', operator: 'equals', value: 'GMP' }
 * ])
 */
export function buildMultiFieldQuery(
  rules: Array<{ field: string; operator: FilterOperator; value: string }>,
  groupOperator: LogicalOperator = 'AND'
): AdvancedSearchQuery {
  return {
    filterGroups: [
      {
        id: '1',
        logicalOperator: 'AND',
        rules: rules.map((rule, index) => ({
          id: String(index + 1),
          field: rule.field,
          operator: rule.operator,
          value: rule.value,
        })),
      },
    ],
    groupOperator,
  };
}

/**
 * Create an empty filter group for initialization
 * @returns Empty FilterGroup structure
 */
export function createEmptyFilterGroup(id: string = '1'): FilterGroup {
  return {
    id,
    logicalOperator: 'AND',
    rules: [],
  };
}

/**
 * Create an empty filter rule for initialization
 * @returns Empty FilterRule structure
 */
export function createEmptyFilterRule(id: string = '1'): FilterRule {
  return {
    id,
    field: '',
    operator: 'contains',
    value: '',
  };
}
